/* eslint-disable no-restricted-syntax */
/* eslint-disable no-underscore-dangle */
import lod_ from "lodash";
import React, {
	useEffect,
	useState,
	useMemo,
	useCallback,
	forwardRef,
	useImperativeHandle
} from "react";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import IconButton from "@mui/material/IconButton";
import MoreVert from "@mui/icons-material/MoreVert";
import {
	setAssets,
	selectAssets,
	selectAssetsByFolderId,
	deleteAsset
} from "../../../../redux-react/reducers/assetReducer";
import { display } from "redux-react/reducers/snackBarReducer";
import { selectRootFolderBySubchildId } from "../../../../redux-react/reducers/foldersReducer";
import store from "../../../../redux-react/store";
import DefaultDataTable from "components/Custom/Tables/DefaultDataTable";
import DialogCreateAsset from "../DialogAsset/DialogCreateAsset";
import ConfirmDialog from "components/Custom/Dialogs/ConfirmDialog";
import EmptyAsset from "./EmptyAsset";
import MoveFile from "./MoveFile/MoveFile";
import assetColumns from "./ColumnTableAsset";
import AssetMenu from "./AssetMenu";
import AssetDetailsDrawer from "./AssetDetailsDrawer";
import { socket } from "redux-react/middleware/ws";
import { PUBLIC } from "contants";
import MDBox from "components/Basics/MDBox";
import { Badge, Divider, Icon, Menu, MenuItem } from "@mui/material";
import MDButton from "components/Basics/MDButton";
import SourceActions from "redux-react/actions/sourceActions";
import MDTypography from "components/Basics/MDTypography";
import MetadatasDialog from "../MetadatasDialog";
import { queryDictionary } from "helpers/dictionary";
import ChangeStatusDialog from "./ChangeStatus/ChangeStatus";
import ChangeAccessDialog from "./ChangeAccess/ChangeAccess";

/**
 * @param {Object} props
 * @param {string} props.folderId
 * @param {string} props.assistantID
 * @param {Array} props.formattedFolders
 * @param {string[]} props.filteredFolderIds
 * @param {boolean} props.isWritter
 * @param {boolean} props.isReviewer
 * @param {boolean} props.isPublisher
 * @param {Object} props.activeFilters
 * @param {string[]} props.activeFilters.status
 * @param {string[]} props.activeFilters.access
 * @param {string[]} props.activeFilters.type
 * @param {Date} [props.activeFilters.dateStart]
 * @param {Date} [props.activeFilters.dateEnd]
 * @param {boolean} props.canCreateAsset
 * @param {boolean} props.canDeleteAsset
 * @param {boolean} props.canEditAsset
 * @param {boolean} props.canChangeStatus
 * @param {string[]} props.folderIdsFiltered
 */
const Assets = forwardRef(
	(
		{
			folderId,
			assistantID,
			formattedFolders,
			isWritter,
			isReviewer,
			isPublisher,
			activeFilters,
			canCreateAsset,
			canDeleteAsset,
			canEditAsset,
			canChangeStatus,
			folderIdsFiltered,
			searchText,
			acceptedFile
		},
		ref
	) => {
		const dispatch = useDispatch();
		const assets = useSelector(state => selectAssets(state));
		const assetsByFolderId = useSelector(state => selectAssetsByFolderId(state, folderId));

		const [metadatasDictionary, setMetadatasDictionary] = useState({});

		const [dialogs, setDialogs] = useState({
			create: false,
			delete: false,
			move: false,
			details: false,
			faiq: false
		});

		const [localState, setLocalState] = useState({
			loading: true,
			selectedAsset: null,
			menuRef: null
		});

		const handleGetAssets = useCallback(
			(filter = null, id = null) => {
				setLocalState(prev => {
					return { ...prev, loading: true };
				});
				dispatch(
					SourceActions.getAssets(
						"all",
						assistantID,
						response => {
							if (response?.assets) {
								const refeshedAssets = response.assets?.data?.map(it => ({ ...it, id: it?._id }));
								dispatch(setAssets(refeshedAssets));
								// Refresh asset in local state
								if (id) {
									const found = refeshedAssets.find(it => it._id === id);
									if (found) {
										setLocalState(prev => ({ ...prev, selectedAsset: found }));
									}
								}
							}
							setLocalState(prev => ({ ...prev, loading: false }));
						},
						filter
					)
				);
			},
			[dispatch, assistantID]
		);

		useImperativeHandle(ref, () => ({
			sortDate: filtre => {
				handleGetAssets(filtre);
			}
		}));

		const handleAssetAction = useCallback(action => {
			setLocalState(prev => ({ ...prev, menuRef: null }));
			setDialogs(prev => ({
				...prev,
				[action]: true
			}));
		}, []);

		const handleCloseDialog = useCallback(dialog => {
			setDialogs(prev => ({
				...prev,
				[dialog]: false
			}));
			if (dialog === "create") setLocalState(prev => ({ ...prev, selectedAsset: null }));
		}, []);

		const handleDeleteConfirm = useCallback(() => {
			dispatch(
				SourceActions.deleteAsset(localState.selectedAsset._id, assistantID, () => {
					dispatch(deleteAsset(localState.selectedAsset._id));
				})
			);
			handleCloseDialog("delete");
		}, [dispatch, assistantID, localState.selectedAsset]);

		const tableRows = useMemo(() => {
			const lowerQuery = searchText.toLowerCase();
			let result = folderId ? assetsByFolderId : assets;
			const { status, access, type, dateStart, dateEnd } = activeFilters;
			if (status?.length) result = result.filter(asset => status.includes(asset.status));
			if (access?.length) result = result.filter(asset => access.includes(asset.access));
			if (type?.length) result = result.filter(asset => type.includes(asset.fileType));

			return result
				.map(asset => ({
					...asset,
					onViewDetails: asset => {
						setLocalState(prev => ({
							...prev,
							selectedAsset: asset
						}));
						setDialogs(prev => ({
							...prev,
							details: true
						}));
					}
				}))
				.filter(
					it =>
						folderIdsFiltered?.includes(it?.folderId) &&
						it.name?.toLowerCase()?.includes(lowerQuery)
				);
		}, [assets, assetsByFolderId, activeFilters, folderIdsFiltered, searchText]);

		useEffect(() => {
			handleGetAssets({ sortedBy: "name", asc: 1 });
		}, [handleGetAssets]);

		const columns = useMemo(
			() =>
				assetColumns(t, {
					isWritter,
					isReviewer,
					isPublisher,
					canChangeStatus,
					reloadTable: handleGetAssets
				}),
			[t, isWritter, isReviewer, isPublisher, canChangeStatus]
		);

		const downloadFiles = useCallback(
			data => {
				if (!data?.content) return;

				let fileCount = 0;

				data.content.forEach(contentItem => {
					Object.entries(contentItem).forEach(([lang, langData]) => {
						fileCount += langData?.attachments?.length || 0;
						langData?.attachments?.forEach(file => {
							window.open(file.url, "_blank");
						});
					});
				});

				dispatch(
					display({
						type: "info",
						message: t("ASSETS.ACTIONS.downloading_files", { count: fileCount })
					})
				);
			},
			[dispatch, t]
		);

		const [selectedRows, setSelectedRows] = useState([]);
		const [anchorElMenu, setAnchorElMenu] = useState(null);

		const [metadatasDialog, setMetadatasDialog] = useState({
			open: false,
			assets: []
		});

		const [moveFilesDialog, setMoveFilesDialog] = useState({
			open: false,
			files: []
		});

		const [changeStatusDialog, setChangeStatusDialog] = useState({
			open: false,
			listIds: []
		});

		const [changeAccessDialog, setChangeAccessDialog] = useState({
			open: false,
			listIds: []
		});

		// Delete confirm dialog
		const [confirmDialog, setConfirmDialog] = useState({
			open: false,
			title: "",
			content: "",
			handleConfirm: () => {}
		});

		/**
		 * Delete many sources
		 * @param {*} sources - List of sources code to delete
		 */
		const actionDeleteManyAssets = assetsID => {
			const handleConfirmDialog = async () => {
				setConfirmDialog({
					open: false
				});

				dispatch(
					SourceActions.deleteAssets(assetsID, () => {
						for (let assetId of assetsID) {
							dispatch(deleteAsset(assetId));
						}
					})
				);
			};

			setConfirmDialog({
				open: true,
				title: `${t("SETTINGS.delete")}`,
				content: `${t("SETTINGS.deleteConfirmation")} ces assets ?`,
				handleConfirm: handleConfirmDialog
			});
		};

		const convertAssetsToSource = list => {
			dispatch(
				SourceActions.convertAssetsToSource(
					list,
					{},
					res => {},
					err => {}
				)
			);
		};

		const canMoveFiles = () => {
			// Get root folder for each selected asset using its folderId
			const rootFoldersForSelectedRows = selectedRows.map(row => {
				const state = store.getState();
				const rootFolder = selectRootFolderBySubchildId(state, row.folderId);
				return rootFolder;
			});

			// Get unique root folder IDs
			const uniqueRootFolderIds = [
				...new Set(
					rootFoldersForSelectedRows.filter(Boolean).map(folder => folder.id || folder._id)
				)
			];

			// All selected assets must be in the same root folder to allow moving
			return uniqueRootFolderIds.length === 1;
		};

		const getRootFolderKey = () => {
			// Get root folder for each selected asset using its folderId
			const rootFoldersForSelectedRows = selectedRows.map(row => {
				const state = store.getState();
				const rootFolder = selectRootFolderBySubchildId(state, row.folderId);
				return rootFolder;
			});

			// Get unique root folder IDs
			const uniqueRootFolders = [
				...new Set(rootFoldersForSelectedRows.filter(Boolean).map(folder => folder))
			];

			if (uniqueRootFolders.length === 1) {
				return uniqueRootFolders[0].key;
			}
			return null;
		};

		const getRootFolderKeyByFolderId = folderIdParam => {
			const state = store.getState();
			const rootFolder = selectRootFolderBySubchildId(state, folderIdParam);
			return rootFolder?.key || null;
		};

		const HEADER_ACTIONS = [
			<MDBox>
				<Badge
					badgeContent={selectedRows.length}
					color="error"
					anchorOrigin={{
						vertical: "top",
						horizontal: "right"
					}}
				>
					<MDButton
						sx={{ ml: 2 }}
						size="small"
						disabled={lod_.isEmpty(selectedRows)}
						variant="contained"
						color="dark"
						onClick={e => {
							setAnchorElMenu(e.currentTarget);
						}}
					>
						<Icon>list_alt</Icon>&nbsp;Actions
					</MDButton>
				</Badge>
				{/* Menu */}
				<Menu
					anchorEl={anchorElMenu}
					open={Boolean(anchorElMenu)}
					onClose={() => setAnchorElMenu(null)}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "center"
					}}
					transformOrigin={{
						vertical: "top",
						horizontal: "center"
					}}
				>
					<MenuItem disabled style={{ opacity: 1 }}>
						<MDTypography variant="caption">{t(`FAIQ.TESTING.actions`)}</MDTypography>
					</MenuItem>
					{/* Extract */}
					<MenuItem
						disabled={selectedRows.length < 1}
						onClick={() => {
							const list = selectedRows.map(row => row.code);
							convertAssetsToSource(list);

							setAnchorElMenu(null);
							setSelectedRows([]);
						}}
					>
						<Icon fontSize="medium">model_training</Icon>&nbsp; {t("SETTINGS.train")}
					</MenuItem>
					{/* Metadatas */}
					<MenuItem
						onClick={() => {
							const list = selectedRows.map(row => {
								return {
									_id: row._id,
									sources: row.faiq?.codes || []
								};
							});

							setMetadatasDialog({
								open: true,
								assets: list
							});
							setAnchorElMenu(null);
							setSelectedRows([]);
						}}
					>
						<Icon fontSize="medium">storage</Icon>&nbsp;
						{t("SOURCES.ACTIONS.extractMetadatas")}
					</MenuItem>
					{/* Move */}
					<MenuItem
						disabled={!canMoveFiles()}
						onClick={() => {
							const key = getRootFolderKey();

							setMoveFilesDialog({
								open: true,
								files: selectedRows,
								key
							});

							setAnchorElMenu(null);
							setSelectedRows([]);
						}}
					>
						<Icon fontSize="medium">drive_file_move</Icon>&nbsp;{t("SETTINGS.move")}
					</MenuItem>
					{/* Move */}
					<MenuItem
						onClick={() => {
							const listIds = selectedRows.map(row => row._id);

							setChangeStatusDialog({
								open: true,
								listIds
							});

							setAnchorElMenu(null);
							setSelectedRows([]);
						}}
					>
						<Icon fontSize="medium">change_circle</Icon>&nbsp;
						{t("ASSETS.ACTIONS.changeStatusButton")}
					</MenuItem>
					{/* Access */}
					<MenuItem
						onClick={() => {
							const listIds = selectedRows.map(row => row._id);

							setChangeAccessDialog({
								open: true,
								listIds
							});

							setAnchorElMenu(null);
							setSelectedRows([]);
						}}
					>
						<Icon fontSize="medium">lock</Icon>&nbsp;
						{t("ASSETS.ACTIONS.changeAccessButton")}
					</MenuItem>
					{/*  Delete */}
					<Divider />
					<MenuItem
						onClick={() => {
							const list = selectedRows.map(row => row._id);
							actionDeleteManyAssets(list);
							setAnchorElMenu(null);
							setSelectedRows([]);
						}}
						style={{
							color: "red"
						}}
					>
						<Icon fontSize="medium">delete</Icon>&nbsp;Supprimer
					</MenuItem>
				</Menu>
			</MDBox>
		];

		const table = useMemo(
			() => (
				<DefaultDataTable
					fixedLayout
					list={null}
					placeHolderEmptyMessage={
						<MDBox
							display="flex"
							flexDirection="column"
							justifyContent="center"
							alignItems="center"
						>
							<MDTypography
								variant="h4"
								color="text"
								sx={{
									opacity: 0.6
								}}
							>
								{t("ASSETS.ACTIONS.emptyFolder")}
							</MDTypography>
							<MDTypography
								mt={1}
								variant="body2"
								color="text"
								sx={{
									opacity: 0.5
								}}
								fontWeight="bold"
							>
								{t("ASSETS.ACTIONS.emptyFolderDescription")}
							</MDTypography>
						</MDBox>
					}
					onRowClick={(row, original) => {
						original.onViewDetails?.(original);
					}}
					headerAction={HEADER_ACTIONS}
					compact
					noMargin
					checkbox
					selectedRows={selectedRows}
					handleSelection={values => {
						setSelectedRows(values);
						// onChange(values);
					}}
					isSelectedRow={row => {
						const existingRow = selectedRows.find(r => r._id === row._id);
						return Boolean(existingRow);
					}}
					checkboxUniqueValue="_id"
					table={{
						columns,
						rows: tableRows,
						count: tableRows.length,
						isSorted: true
					}}
					isSorted
					onSorted={(val, id) =>
						handleGetAssets(
							id === 0 ? { sortedBy: "updatedAt", asc: -1 } : { sortedBy: val, asc: id }
						)
					}
					pagination={{ defaultEntrie: 50, entriesPerPage: [25, 50, 100, 200] }}
					actions={[
						<IconButton
							key="action-1"
							handleclick={({ original }, e) => {
								setLocalState(prev => ({
									...prev,
									menuRef: e.currentTarget,
									selectedAsset: original
								}));
								e.preventDefault();
								e.stopPropagation();
							}}
						>
							<MoreVert />
						</IconButton>
					]}
				/>
			),
			[tableRows, selectedRows, anchorElMenu]
		);

		const fetchMetadatasDictionary = async () => {
			const dictionary = await queryDictionary(dispatch, "metadatas");
			setMetadatasDictionary(dictionary);
		};

		const wsReloadTable = () => {
			handleGetAssets({ sortedBy: "name", asc: 1 });
		};

		useEffect(() => {
			fetchMetadatasDictionary();
		}, []);

		useEffect(() => {
			socket.on("assets_reload_table", wsReloadTable);

			return () => {
				socket.off("assets_reload_table", wsReloadTable);
			};
		}, []);

		if (folderId && !tableRows?.length && !localState.loading && (isReviewer || isPublisher)) {
			return (
				<>
					<EmptyAsset onAddAsset={() => setDialogs(prev => ({ ...prev, create: true }))} />
					{dialogs.create && (
						<DialogCreateAsset
							folderId={folderId}
							assistantID={assistantID}
							asset={localState.selectedAsset}
							onClose={() => handleCloseDialog("create")}
							acceptedFile={acceptedFile}
						/>
					)}
				</>
			);
		}

		return (
			<>
				{/* Dialog to manage metadatas */}
				{metadatasDialog.open && (
					<MetadatasDialog
						open={metadatasDialog.open}
						onClose={() => {
							setMetadatasDialog({
								open: false,
								sources: []
							});
						}}
						assets={metadatasDialog.assets}
						metadatasDictionary={metadatasDictionary}
						reloadAssets={handleGetAssets}
					/>
				)}
				{/* Dialog to confirm delete */}
				<ConfirmDialog
					open={confirmDialog.open}
					title={confirmDialog.title}
					content={confirmDialog.content}
					handleClose={() => {
						setConfirmDialog({
							open: false
						});
					}}
					handleConfirm={confirmDialog.handleConfirm}
				/>
				{table}
				<AssetDetailsDrawer
					open={dialogs.details}
					onClose={() => handleCloseDialog("details")}
					asset={localState.selectedAsset}
					setLocalState={setLocalState}
					formattedFolders={formattedFolders}
					reloadAssets={handleGetAssets}
					readMore
				/>
				<AssetMenu
					anchorEl={localState.menuRef}
					onClose={() => setLocalState(prev => ({ ...prev, menuRef: null }))}
					onMove={() => handleAssetAction("move")}
					onEdit={() => handleAssetAction("create")}
					onViewDetails={() => handleAssetAction("details")}
					onDelete={() => handleAssetAction("delete")}
					onShare={e => {
						e.stopPropagation();
						navigator.clipboard
							.writeText(localState.selectedAsset?.accessUrl)
							.then(() => {
								dispatch(
									display({
										type: "success",
										message: t("ASSETS.TABLE.urlCopied")
									})
								);
							})
							.catch(err => {
								dispatch(
									display({
										type: "error",
										message: t("ASSETS.TABLE.urlCopyFailed")
									})
								);
							});
						setLocalState(prev => ({ ...prev, menuRef: null }));
					}}
					t={t}
					isWritter={isWritter}
					isReviewer={isReviewer}
					isPublisher={isPublisher}
					canShare={
						localState.selectedAsset?.access === PUBLIC &&
						!lod_.isNil(localState.selectedAsset?.accessUrl)
					}
					isAccesUrl={!lod_.isNil(localState.selectedAsset?.accessUrl)}
					onDownloadFile={() => downloadFiles(localState.selectedAsset)}
					canCreateAsset={canCreateAsset}
					canDeleteAsset={canDeleteAsset}
					canEditAsset={canEditAsset}
					fileType={localState.selectedAsset?.fileType}
				/>
				{dialogs.create && (
					<DialogCreateAsset
						folderId={folderId}
						assistantID={assistantID}
						asset={localState.selectedAsset}
						isEdit
						onClose={() => handleCloseDialog("create")}
					/>
				)}
				<ConfirmDialog
					title={t("ASSETS.DELETE_CONFIRMATION.title")}
					content={t("ASSETS.DELETE_CONFIRMATION.message", {
						name: localState.selectedAsset?.name
					})}
					open={dialogs.delete}
					handleClose={() => handleCloseDialog("delete")}
					handleConfirm={handleDeleteConfirm}
					isDelete
				/>
				{dialogs.move && (
					<MoveFile
						formattedFolders={formattedFolders ?? []}
						onClose={() => handleCloseDialog("move")}
						selectedAssets={[localState.selectedAsset]}
						reloadAssets={handleGetAssets}
						folderKey={getRootFolderKeyByFolderId(localState?.selectedAsset?.folderId)}
					/>
				)}
				{/* move files from table action */}
				{moveFilesDialog.open && (
					<MoveFile
						formattedFolders={formattedFolders ?? []}
						onClose={() => {
							setMoveFilesDialog({
								open: false,
								files: [],
								key: null
							});
						}}
						selectedAssets={moveFilesDialog.files}
						reloadAssets={handleGetAssets}
						folderKey={moveFilesDialog.key}
					/>
				)}

				{/* change status from table action */}
				<ChangeStatusDialog
					open={changeStatusDialog.open}
					listIds={changeStatusDialog.listIds}
					onClose={() => {
						setChangeStatusDialog({
							open: false
						});
					}}
					reloadAssets={handleGetAssets}
				/>

				{/* Change access from table action */}
				<ChangeAccessDialog
					open={changeAccessDialog.open}
					listIds={changeAccessDialog.listIds}
					onClose={() => {
						setChangeAccessDialog({
							open: false
						});
					}}
					reloadAssets={handleGetAssets}
				/>
			</>
		);
	}
);

export default Assets;
